import { memo, useMemo } from 'react'
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { useParams } from 'react-router'

import {
  CategoryChildrens,
  CategoryParent,
} from '@entities/Categories'
import { useCategoriesBranch } from '@entities/Categories/api/categoriesBranch.gql'

import { useAddressStore } from '@features/shipping-methods'

import { cn } from '@shared/lib/cn'
import Accordion from '@shared/ui/accordion'

import styles from './styles.module.scss'

interface CategoriesSidebarProps {
  className?: string;
}

// Мемоизированный компонент загрузки
const CategoriesSidebarLoading = memo(function CategoriesSidebarLoading () {
  return (
    <SkeletonTheme>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        {Array.from({ length: 12 }).map((_, index) => (
          <div
            key={index}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <Skeleton
              count={1}
              width={32}
              height={32}
              style={{ borderRadius: 8 }}
            />
            <Skeleton
              count={1}
              width={index % 2 === 0 ? 130 : 100}
              height={16}
              style={{ borderRadius: 6 }}
            />
          </div>
        ))}
      </div>
    </SkeletonTheme>
  )
})

// Мемоизированный основной компонент
const CategoriesSidebar = memo(function CategoriesSidebar (
  props: CategoriesSidebarProps,
) {
  const { categorySlug } = useParams()
  const { className } = props
  const { shopId } = useAddressStore()

  // Запрос данных
  const { categoriesBranch, loading } = useCategoriesBranch({
    shopId
  })

  // Мемоизация списка категорий
  const categories = useMemo(() => {
    return categoriesBranch || []
  }, [ categoriesBranch ])

  // Мемоизация рендеринга элементов
  const renderedItems = useMemo(() => {
    return categories.map((item) => {
      const isOpen = categorySlug === item?.slug
      const hasChildren = item?.childrens && item.childrens.length > 0

      // Если нет дочерних категорий — рендерим как ссылку
      if (!hasChildren) {
        return (
          <div className={styles.root__item} key={item?.id}>
            <CategoryParent
              name={item?.name}
              iconUrl={item?.miniIcon?.url ?? item?.icon?.url}
              to={`/catalog/${item?.slug}`}
            />
          </div>
        )
      }

      // Если есть дочерние — рендерим аккордеон
      return (
        <div className={styles.root__item} key={item?.id}>
          <Accordion
            className={styles.accordion}
            triggerClassName={styles['root__parent-link']}
            trigger={
              <CategoryParent
                name={item?.name}
                iconUrl={item?.miniIcon?.url ?? item?.icon?.url}
              />
            }
            content={
              <CategoryChildrens
                parentSlug={item?.slug}
                items={item?.childrens ?? []}
              />
            }
            arrowIcon={false}
            defaultOpen={isOpen}
          />
        </div>
      )
    })
  }, [ categories, categorySlug ])

  if (loading) {
    return <CategoriesSidebarLoading />
  }

  return <div className={cn(styles.root, className)}>{renderedItems}</div>
})

export default CategoriesSidebar
